import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { from, Observable, switchMap } from 'rxjs';
import { DpopService } from '@core/services/dpop.service';

const isTokenEndpoint = (url: string): boolean => /\/v1\/token(\?|$)/.test(url);

const PUBLIC_ENDPOINTS = [
  {
    method: 'GET',
    pattern: /\/(?:api\/)?v1\/financial-institutions(?:\?|$)/,
  },
];

const isPublicEndpoint = (url: string, method: string): boolean => {
  const normalizedMethod = method.toUpperCase();
  return PUBLIC_ENDPOINTS.some(
    (endpoint) => endpoint.method === normalizedMethod && endpoint.pattern.test(url)
  );
};

const extractBearerToken = (authHeader: string | null): string | undefined => {
  if (!authHeader) return undefined;
  const match = authHeader.match(/^Bearer\s+(.+)$/i);
  return match ? match[1] : undefined;
};

/**
 * Adds DPoP proof JWT to all requests targeting the API (/v1/).
 *
 * Token endpoint:  DPoP header only (no ath, no Authorization change).
 * Resource calls:  DPoP header with ath; Authorization scheme changed from Bearer → DPoP.
 */
@Injectable()
export class DpopInterceptor implements HttpInterceptor {
  constructor(private readonly dpopService: DpopService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!req.url.includes('/v1/') || isPublicEndpoint(req.url, req.method)) {
      return next.handle(req);
    }

    return from(this.buildDpopRequest(req)).pipe(switchMap((cloned) => next.handle(cloned)));
  }

  private async buildDpopRequest(req: HttpRequest<unknown>): Promise<HttpRequest<unknown>> {
    const isToken = isTokenEndpoint(req.url);
    const currentAuth = req.headers.get('Authorization');
    const accessToken = isToken ? undefined : extractBearerToken(currentAuth);

    const proof = await this.dpopService.createProof(req.method, req.url, accessToken);

    if (isToken) {
      // Token endpoint: only add DPoP header, do not alter Authorization
      return req.clone({ setHeaders: { DPoP: proof } });
    }

    // Resource call: add DPoP header + swap Bearer → DPoP scheme
    const updatedHeaders: Record<string, string> = { DPoP: proof };
    if (accessToken) {
      updatedHeaders['Authorization'] = `DPoP ${accessToken}`;
    }
    return req.clone({ setHeaders: updatedHeaders });
  }
}
